import { IssueParams } from 'components/common/forms/EditableField/types';
import { SubmitOptions } from 'components/dataCreationForms/withInputForm';
import { SetableInspectionFields } from 'store/inspection';
import { InspectionTemplateItemModel } from 'components/dataProviders/withRequiredInspectionTemplate/types';
import { EntityName } from 'shared/domain/fieldValue/fields';
import { LevelOnView } from 'shared/domain/level/types/view';
import { HashMap, LabelledEntity } from 'shared/types/commonView';
import { Permission } from 'shared/domain/permissions/types';
import { ReactNode } from 'react';
import { InspectionStatus } from 'shared/domain/inspection/inspectionModel';

type ExtractorDependencyValue =
  | readonly LabelledEntity[]
  | LabelledEntity
  | undefined;

export type LevelsFieldExtractor = (
  available: HashMap<LevelOnView[]>,
  dependency: ExtractorDependencyValue
) => LabelledEntity[] | undefined;

export type LabelledEntitiesFieldExtractor = (
  available: HashMap<LabelledEntity[]>,
  dependency: ExtractorDependencyValue
) => LabelledEntity[] | undefined;

export type FormFieldExtractor =
  | LevelsFieldExtractor
  | LabelledEntitiesFieldExtractor;

type MappedAvailableItems =
  | HashMap<LevelOnView[]>
  | HashMap<LabelledEntity[]>;

export type InspectionFormSection =
  InspectionTemplateItemModel['sections'][number] & {
    inspectionDescription?: string;
  };

export interface InspectionFormProps {
  loading: boolean;
  onSubmit?: (options?: SubmitOptions) => void;
  onCancel?: () => void;
  isSaving?: boolean;
  atomicMode?: boolean;
  editPermission?: Permission;
  actionButtons?: ReactNode;
  inspectionStatus?: InspectionStatus;
  onStatusChange?: (status: InspectionStatus) => void;
  isArchived?: boolean;
}

export interface InspectionFormPresentationalProps {
  template: InspectionTemplateItemModel;
  onSubmit?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  isNewInspection: boolean;
  inspectionCode?: string;
  atomicMode?: boolean;
  editPermission?: Permission;
  actionButtons?: ReactNode;
  inspectionStatus?: InspectionStatus;
  onStatusChange?: (status: InspectionStatus) => void;
  isArchived?: boolean;

  executionFieldProps: InspectionFieldProps;
  workTypesFieldProps: InspectionFieldProps;
  sitesFieldProps: InspectionFieldProps;
  levelsFieldProps: InspectionFieldProps;
  controlledPartiesFieldProps: InspectionFieldProps;
  contractsFieldProps: InspectionFieldProps;
  commentFieldProps: InspectionFieldProps;
  checklist: InspectionTemplateItemModel['checklist'];
  sections: InspectionFormSection[];
  protocolIssues: number;
}

export type InspectionFieldProps = FormFieldProps<SetableInspectionFields>;
interface Common<T> {
  formKey: keyof T;
  required: boolean;
  labelId: string;
  fieldName: string;
  localStorageKey?: string;
  disabled?: boolean | { reason: string };
  available?: LabelledEntity[] | MappedAvailableItems;
  getOptionLabel?: (option: LabelledEntity) => string;
  getOptionSelected?: (
    option: LabelledEntity,
    value: LabelledEntity
  ) => boolean;
  getOptionDisabled?: (option: LabelledEntity) => boolean;
  dependsOn?: keyof T;
  extractor?: FormFieldExtractor;
  groupBy?: (option: LabelledEntity & { group?: string }) => string;
  maxVisibleTags?: number;
  minRows?: number;
  disableClearable?: boolean;
  autoSelect?: 'asSingleSelect' | 'asMultiSelect';
  multiline?: boolean;
  reserveSpaceForHelperText: boolean;
  dense: boolean;
  'data-qa': string;
  issueParams: IssueParams | null;
}
export type FormFieldProps<T> = {
  minDate?: Date | null;
  maxDate?: Date | null;
  type?: 'number';
} & Common<T>;

export type MultiChoiceFormFieldProps<T> = {
  entityName: EntityName | '';
} & Common<T>;

export type SingleChoiceFormFieldProps<T> = MultiChoiceFormFieldProps<T>;

export enum InspectionFieldName {
  inspectionDate = 'inspection-date',
  multiselectWorkTypes = 'multiselect-worktypes',
  multiselectSites = 'multiselect-sites',
  multiselectLevels = 'multiselect-levels',
  multiselectControlledParties = 'multiselect-controlled-parties',
  multiselectContracts = 'multiselect-contracts',
  textFieldComment = 'text-field-comment',
}
